import type { MarginRow } from '../api/types'

/**
 * Small bar chart of top-N by true margin %. Intentionally primitive —
 * uses div bars sized as percentages. M6 does the same (spec §11.4).
 */
export function MarginSummaryChart({
  rows,
  topN = 10,
}: {
  rows: MarginRow[]
  topN?: number
}) {
  // Exclude rows with null true_margin from the ranking — a null value
  // isn't "a zero margin," it's "we don't know" and shouldn't seed a
  // bar position. Backend may return nulls when a grouping lacks the
  // data (per MarginRow type nullable markers).
  const ranked = rows.filter(
    (r): r is MarginRow & { true_margin: number; true_margin_pct: number } =>
      r.true_margin != null && r.true_margin_pct != null,
  )
  const top = [...ranked]
    .sort((a, b) => b.true_margin - a.true_margin)
    .slice(0, topN)
  const max = Math.max(1, ...top.map((r) => Math.abs(r.true_margin)))

  return (
    <div className="mx-auto flex max-w-[720px] flex-col gap-3">
      <h2 className="text-lg font-semibold text-primary">Top {topN} by true margin $</h2>
      <ul className="flex flex-col gap-2">
        {top.map((r) => {
          const width = `${(Math.abs(r.true_margin) / max) * 100}%`
          const negative = r.true_margin < 0
          return (
            <li key={r.group_key ?? ''} className="flex items-center gap-3">
              <span className="w-52 truncate text-xs text-tertiary">
                {r.group_key ?? '—'}
              </span>
              <span
                className={
                  negative
                    ? 'h-4 rounded-xs bg-danger-bg'
                    : 'h-4 rounded-xs bg-accent'
                }
                style={{ width, minWidth: '2px' }}
                aria-hidden="true"
              />
              <span className="min-w-16 text-right text-xs text-secondary tabular-nums">
                {r.true_margin.toLocaleString(undefined, {
                  style: 'currency',
                  currency: 'USD',
                  maximumFractionDigits: 0,
                })}
              </span>
              <span className="min-w-12 text-right text-role-metadata tabular-nums">
                {r.true_margin_pct.toFixed(1)}%
              </span>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
